// /home/project/src/components/ErrorBoundary.tsx
import { Component, ErrorInfo, ReactNode } from "react";
import { AppIcon } from '@/components/ui/icons';
import { Button } from "../features/ui/components/Button";

interface Props {
  children: ReactNode;
  /** Render your own fallback; if omitted, a branded one is shown */
  fallback?: ReactNode;
  /** Called when user clicks “Try again” */
  onReset?: () => void;
  /** When any value in this array changes, the boundary resets automatically */
  resetKeys?: Array<string | number | boolean | null | undefined>;
  /** Smaller fallback layout (e.g., inside panels) */
  compact?: boolean;
  /** Optional error hook for logging */
  onError?(_error: Error, info: ErrorInfo): void;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  info?: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // store both error and info so the fallback can display details
    this.setState({ error, info });
    // send to app-level logger if provided
    this.props.onError?.(error, info);
    // still helpful in dev
    if (process.env.NODE_ENV === 'development') {
      console.error("Component crashed:", error, info);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys } = this.props;
    if (!this.state.hasError || !resetKeys) return;

    const changed =
      !prevProps.resetKeys ||
      resetKeys.length !== prevProps.resetKeys.length ||
      resetKeys.some((v, i) => v !== prevProps.resetKeys![i]);

    if (changed) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
    this.props.onReset?.();
  };

  reload = () => {
    window.location.reload();
  };

  copyDetails = async () => {
    const { error, info } = this.state;
    const details =
      `Error: ${error?.name}: ${error?.message}\n\n` +
      (error?.stack ? `Stack:\n${error.stack}\n\n` : "") +
      (info?.componentStack ? `Component stack:\n${info.componentStack}` : "");
    try {
      await navigator.clipboard.writeText(details);
      // no toast system here—silent success
    } catch {
      // ignore
    }
  };

  renderFallback() {
    const { fallback, compact } = this.props;
    if (fallback) return fallback;

    const inDev = process.env.NODE_ENV === 'development';
    const { error, info } = this.state;

    return (
      <div
        className={[
          "mx-auto",
          compact ? "p-3" : "card-padding",
          "rounded-lg border border-border-subtle",
          "bg-surface-card shadow-card",
          "text-text-primary",
        ].join(" ")}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-status-danger-soft flex items-center justify-center">
            <AppIcon name="alertTriangle" className="w-5 h-5 text-status-danger-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold">Something went wrong</h3>
            <p className="text-sm text-muted mt-1">
              The component failed to render. You can try again or reload the page.
            </p>

            {inDev && (error || info) && (
              <div className="mt-3 rounded-lg bg-surface-muted p-3 overflow-auto max-h-48 text-xs">
                <div className="flex items-center gap-2 mb-sm text-text-secondary">
                  <AppIcon name="bug" className="w-4 h-4" />
                  <span>Debug details (dev only)</span>
                </div>
                {error?.message && (
                  <pre className="whitespace-pre-wrap">{error.name}: {error.message}</pre>
                )}
                {error?.stack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">Stack trace</summary>
                    <pre className="mt-1 whitespace-pre-wrap">{error.stack}</pre>
                  </details>
                )}
                {info?.componentStack && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">Component stack</summary>
                    <pre className="mt-1 whitespace-pre-wrap">{info.componentStack}</pre>
                  </details>
                )}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="primary" size={compact ? "sm" : "md"} onClick={this.reset} startIcon={<AppIcon name="rotateCcw" className="w-4 h-4" />}>
                Try again
              </Button>
              <Button variant="secondary" size={compact ? "sm" : "md"} onClick={this.reload} startIcon={<AppIcon name="refreshCw" className="w-4 h-4" />}>
                Reload page
              </Button>
              {process.env.NODE_ENV === 'development' && (
                <Button
                  variant="outline"
                  size={compact ? "sm" : "md"}
                  onClick={this.copyDetails}
                  startIcon={<AppIcon name="copy" className="w-4 h-4" />}
                >
                  Copy details
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }
    return this.props.children;
  }
}
